import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Wallet, Paperclip, FileText, Pencil, Check, X, Target } from "lucide-react";

const CATEGORIES = ["S&C", "Physio", "Nutrition", "Medical", "Travel", "Equipment", "Coaching", "Other"];
const PERIODS = ["one-off", "weekly", "monthly", "quarterly", "yearly"];

export default function Budget({ athleteId }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ category: "S&C", item: "", amount: "", period: "monthly" });
  const [editId, setEditId] = useState(null);
  const [ef, setEf] = useState({});
  const [goal, setGoalState] = useState({ amount: "", period: "monthly" });
  const [savingGoal, setSavingGoal] = useState(false);
  const fileRefs = useRef({});

  const load = useCallback(() => {
    if (athleteId) api.get(`/athletes/${athleteId}/budget`).then((r) => {
      setD(r.data);
      setGoalState({ amount: r.data.goal?.amount || "", period: r.data.goal?.period || "monthly" });
    });
  }, [athleteId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.item || f.amount === "") return toast.error("Item and amount required");
    await api.post(`/athletes/${athleteId}/budget`, { ...f, amount: Number(f.amount) });
    toast.success("Budget item added"); setF({ ...f, item: "", amount: "" }); load();
  };
  const del = async (id) => { await api.delete(`/athletes/${athleteId}/budget/${id}`); load(); };

  const startEdit = (it) => { setEditId(it.item_id); setEf({ category: it.category, item: it.item, amount: it.amount, period: it.period }); };
  const saveEdit = async (id) => {
    if (!ef.item || ef.amount === "") return toast.error("Item and amount required");
    try {
      await api.put(`/athletes/${athleteId}/budget/${id}`, { ...ef, amount: Number(ef.amount) });
      toast.success("Budget item updated"); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const saveGoal = async () => {
    setSavingGoal(true);
    try {
      await api.post(`/athletes/${athleteId}/budget/goal`, { goal: Number(goal.amount) || 0, period: goal.period });
      toast.success("Budget goal saved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingGoal(false); }
  };

  const uploadInvoice = async (itemId, file) => {
    if (!file) return;
    if (file.size > 2_000_000) return toast.error("Invoice too large (max 2MB)");
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    try {
      await api.post(`/athletes/${athleteId}/budget/${itemId}/invoice`, { name: file.name, type: file.type, data: dataUrl });
      toast.success("Invoice uploaded"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const viewInvoice = async (itemId) => {
    const r = await api.get(`/athletes/${athleteId}/budget/${itemId}/invoice`);
    const w = window.open();
    if (w) w.document.write(`<title>${r.data.name}</title><iframe src="${r.data.data}" style="border:0;width:100%;height:100%;position:fixed;inset:0"></iframe>`);
  };

  if (!d) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const goalAmt = Number(d.goal?.amount) || 0;
  const pct = goalAmt > 0 ? Math.min(100, Math.round((d.total / goalAmt) * 100)) : 0;
  const over = goalAmt > 0 && d.total > goalAmt;

  return (
    <div className="p-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-6">
        <div className="bg-background p-5"><div className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground flex items-center gap-2"><Wallet className="w-4 h-4" /> Total</div><div className="font-heading text-4xl font-black tabular-nums mt-2">₹{d.total.toLocaleString()}</div></div>
        {Object.entries(d.by_category).slice(0, 3).map(([c, v]) => (
          <div key={c} className="bg-background p-5"><div className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">{c}</div><div className="font-heading text-2xl font-black tabular-nums mt-2">₹{v.toLocaleString()}</div></div>
        ))}
      </div>

      {/* GOAL */}
      <div className="border border-border p-5 mb-6" data-testid="budget-goal">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Budget Goal</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1">
            <span className="text-lg font-heading">₹</span>
            <input data-testid="goal-amount" type="number" value={goal.amount} onChange={(e) => setGoalState({ ...goal, amount: e.target.value })} placeholder="Set a budget cap" className="inp w-40" />
          </div>
          <select data-testid="goal-period" value={goal.period} onChange={(e) => setGoalState({ ...goal, period: e.target.value })} className="inp">{PERIODS.filter((p) => p !== "one-off").map((p) => <option key={p} value={p}>{p}</option>)}</select>
          <button data-testid="goal-save" onClick={saveGoal} disabled={savingGoal} className="bg-foreground text-background px-4 py-2 text-sm hover:bg-neutral-800 transition-colors disabled:opacity-50">{savingGoal ? "Saving…" : "Save goal"}</button>
        </div>
        {goalAmt > 0 && (
          <div className="mt-4" data-testid="goal-progress">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-muted-foreground">₹{d.total.toLocaleString()} of ₹{goalAmt.toLocaleString()} {d.goal.period}</span>
              <span className={over ? "text-traffic-red font-bold" : "text-muted-foreground"}>{pct}%{over ? " · over budget" : ""}</span>
            </div>
            <div className="h-2 bg-surface border border-border overflow-hidden">
              <div className={`h-full ${over ? "bg-traffic-red" : pct > 80 ? "bg-traffic-amber" : "bg-traffic-green"}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </div>

      <div className="border border-border divide-y divide-border mb-6" data-testid="budget-list">
        {d.items.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No budget items.</div> :
          d.items.map((it) => (
            editId === it.item_id ? (
              <div key={it.item_id} className="p-3 flex flex-wrap items-center gap-2 text-sm bg-surface" data-testid={`budget-edit-${it.item_id}`}>
                <select value={ef.category} onChange={(e) => setEf({ ...ef, category: e.target.value })} className="inp w-28" data-testid={`edit-category-${it.item_id}`}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
                <input value={ef.item} onChange={(e) => setEf({ ...ef, item: e.target.value })} className="inp flex-1 min-w-[140px]" data-testid={`edit-item-${it.item_id}`} />
                <select value={ef.period} onChange={(e) => setEf({ ...ef, period: e.target.value })} className="inp w-28" data-testid={`edit-period-${it.item_id}`}>{PERIODS.map((p) => <option key={p}>{p}</option>)}</select>
                <input type="number" value={ef.amount} onChange={(e) => setEf({ ...ef, amount: e.target.value })} className="inp w-28" data-testid={`edit-amount-${it.item_id}`} />
                <button data-testid={`save-edit-${it.item_id}`} onClick={() => saveEdit(it.item_id)} className="text-traffic-green hover:opacity-70"><Check className="w-4 h-4" /></button>
                <button data-testid={`cancel-edit-${it.item_id}`} onClick={() => setEditId(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div key={it.item_id} className="p-3 flex items-center gap-4 text-sm" data-testid={`budget-${it.item_id}`}>
                <span className="text-xs uppercase tracking-widest font-bold w-24 text-muted-foreground">{it.category}</span>
                <span className="flex-1">{it.item}</span>
                <span className="text-xs text-muted-foreground">{it.period}</span>
                <span className="font-heading font-bold tabular-nums">₹{it.amount.toLocaleString()}</span>
                {it.invoice ? (
                  <button data-testid={`view-invoice-${it.item_id}`} onClick={() => viewInvoice(it.item_id)} className="text-xs flex items-center gap-1 text-foreground hover:underline"><FileText className="w-3.5 h-3.5" /> Invoice</button>
                ) : (
                  <button data-testid={`upload-invoice-${it.item_id}`} onClick={() => fileRefs.current[it.item_id]?.click()} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"><Paperclip className="w-3.5 h-3.5" /> Invoice</button>
                )}
                <input ref={(el) => (fileRefs.current[it.item_id] = el)} type="file" accept="image/*,application/pdf" className="hidden" data-testid={`invoice-file-${it.item_id}`} onChange={(e) => uploadInvoice(it.item_id, e.target.files[0])} />
                <button data-testid={`edit-budget-${it.item_id}`} onClick={() => startEdit(it)} className="text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`del-budget-${it.item_id}`} onClick={() => del(it.item_id)} className="text-muted-foreground hover:text-traffic-red transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            )
          ))}
      </div>

      <div className="grid sm:grid-cols-5 gap-3" data-testid="budget-form">
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="inp" data-testid="budget-category">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
        <input data-testid="budget-item" value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} placeholder="Item" className="inp sm:col-span-2" />
        <input data-testid="budget-amount" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Amount (₹)" className="inp" />
        <button data-testid="budget-add" onClick={add} className="bg-foreground text-background px-4 py-2 text-sm flex items-center justify-center gap-2 hover:bg-neutral-800 transition-colors"><Plus className="w-4 h-4" /> Add</button>
      </div>
    </div>
  );
}
